import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ACTIVITY_ENTRIES, isSystemActor, useAggregateActivity } from '@/features/audit';

import type { AuditEntry } from '@/shared/api/generated/audit';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';

/** What the audit chain files a connection's entries under. The server's own constant —
 * `AGGREGATE_TYPE` in `connection_service.py` — and a value a seven-year chain is keyed on, so it is
 * effectively public API rather than a string this screen chose. */
export const CONNECTION_AGGREGATE = 'catalog.connection';

/* What has happened to this source, from the audit chain.
 *
 * **No new table was invented for this.** Every mutation in the platform already writes a chain entry
 * naming the actor, the action and the instant; a per-connection activity table would be a second
 * answer to "what happened here" that could disagree with the one an auditor reads. So this is a
 * filtered read of the chain itself.
 *
 * **What it cannot say, and says so.** The listing route does not select `payload` — the snapshot
 * never leaves PostgreSQL on that path — so a line here reports that something happened, to what and
 * by whom, and never its contents. Left unstated, a list of actions with no detail column reads as a
 * list whose entries happen to have no detail, which is the inverse of this codebase's most repeated
 * defect: not prose asserting a control that does not exist, but a control nobody is told about.
 *
 * **Harvests are absent, and that is a boundary rather than an omission.** A harvest's entries are
 * filed under `catalog.HarvestOperation` keyed on the *operation*, not the connection — deliberately,
 * since the operation is what a `Location` header resolves to — so they cannot be reached by
 * filtering on this aggregate. Re-filing them would split a seven-year history into two halves no
 * query spans. The footnote points at where the harvest state is shown instead.
 */
export interface SourceActivityProps {
  readonly connectionId: string;
  /** The clock, passed in for the reason every relative time in this app is: a component that reads
   * it re-renders with a different answer on every unrelated state change. */
  readonly now: Date;
}

export function SourceActivity({ connectionId, now }: SourceActivityProps): ReactElement {
  const { t } = useTranslation();
  const activity = useAggregateActivity(CONNECTION_AGGREGATE, connectionId);
  const entries = activity.data?.items ?? [];

  return (
    <Card title={t('connectionDetail.activity')}>
      {activity.isPending ? <SkeletonRows rows={3} columns={2} /> : null}

      {/* **A failed read is reported as a failed read, never as an empty history.** "Nothing has
          happened to this source" is a claim about the tenant's record, and using it to report our
          own failed request is the third-instance class §9 records for the anomaly and evals screens.
          A missing `audit.chain:read` grant lands here too, which is why the sentence names the
          request rather than the data. */}
      {activity.isError ? (
        <p className="lq-source__note">{t('connectionDetail.activityUnread')}</p>
      ) : null}

      {activity.isSuccess && entries.length === 0 ? (
        <EmptyState
          title={t('connectionDetail.activityNone')}
          guidance={t('connectionDetail.activityNoneGuidance')}
        />
      ) : null}

      {activity.isSuccess && entries.length > 0 ? (
        <>
          <ul className="lq-activity">
            {entries.map((entry) => (
              <li
                key={`${entry.chain_date}#${String(entry.sequence)}`}
                className="lq-activity__row"
              >
                {/* Formatted in the component rather than through ICU, because `i18next-icu` 2.4.4
                    cannot render ICU's `time` type — a message using it comes back as its own source,
                    braces and all. §25 records the bisection. */}
                <span className="lq-activity__when lumiq-numeric">
                  {whenOf(entry, now)}
                </span>
                <span className="lq-activity__what">
                  {t(`connectionDetail.action.${entry.action}`, {
                    defaultValue: t('connectionDetail.action.unknown', {
                      action: entry.action,
                    }),
                  })}
                </span>
                {/* A `system:` actor is an unattended sweep rather than a person, and the two must
                    not look alike: "who did this" has a different answer and a different follow-up
                    for each. The platform writes those prefixes deliberately so an investigation can
                    tell a timer from a human. */}
                <span
                  className={
                    isSystemActor(entry.actor)
                      ? 'lq-activity__actor lq-activity__actor--system'
                      : 'lq-activity__actor'
                  }
                >
                  {entry.actor}
                </span>
              </li>
            ))}
          </ul>
          {entries.length === ACTIVITY_ENTRIES ? (
            /* Truncation says so, with the exact count. There is no pager here, so a full list that
               claimed to be the whole history would be the `next_cursor: null`-while-truncating
               defect in a card. */
            <p className="lq-source__note">
              {t('connectionDetail.activityCapped', { count: ACTIVITY_ENTRIES })}
            </p>
          ) : null}
          <p className="lq-source__note">{t('connectionDetail.activityNoSnapshot')}</p>
        </>
      ) : null}
    </Card>
  );
}

/* An absolute instant for today's entries and a date for older ones.
 *
 * A history read top-to-bottom wants to know "this morning" without arithmetic, and wants a real date
 * once an entry is old enough that a relative age stops being useful — "38 days ago" is a worse answer
 * than "19 Jul 2026" for something a reader may be about to cite. Uses the reader's locale rather than
 * a fixed format, and the reader's own midnight rather than UTC's, which is the call `activityWhen`
 * already makes for the same reason.
 */
function whenOf(entry: AuditEntry, now: Date): string {
  const at = new Date(entry.occurred_at);
  if (Number.isNaN(at.getTime())) return entry.chain_date;
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  return sameDay
    ? at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}
